import api, {AI_GENERATION, ASSISTANT_SESSIONS_TYPE, ASSISTANT_USAGE_TYPE, LIST, STOREFRONT_SETTINGS} from '../Api';
import {IActionResponse} from '../../dal/BaseDAL';
import {ICollectionResponse} from '../../models/ICollectionResponse';
import {
    IAssistantCancelRequest,
    IAssistantCapability,
    IAssistantModel,
    IAssistantSession,
    IAssistantSessionRequest,
    IAssistantTranscript,
    IAssistantUsage,
    IStorefrontGenerationRequest,
} from '../../models/Assistant';

/**
 * The AI assistant surface, mirroring api/ml/assistant/*. Every route is gated on the aiRestyle
 * permission and re-checks the organization entitlement server-side, so a 403 here is a real answer
 * and not something to retry.
 */
const assistantEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        getAssistantCapabilities: build.query<ICollectionResponse<IAssistantCapability>, void>({
            query: () => 'ml/assistant/capabilities',
        }),
        getAssistantModels: build.query<ICollectionResponse<IAssistantModel>, void>({
            query: () => 'ml/assistant/models',
        }),
        getAssistantUsage: build.query<IAssistantUsage, void>({
            query: () => 'ml/assistant/usage',
            providesTags: () => [{type: ASSISTANT_USAGE_TYPE, id: LIST}],
        }),
        /** Transcripts are omitted from this response by design. Fetch one session for its messages. */
        getAssistantSessions: build.query<ICollectionResponse<IAssistantSession>, {capability?: string, count?: number} | void>({
            query: (arg) => {
                const params = new URLSearchParams();
                if (arg && arg.capability) params.append('capability', arg.capability);
                if (arg && arg.count) params.append('count', String(arg.count));
                const qs = params.toString();
                return `ml/assistant/sessions${qs ? `?${qs}` : ''}`;
            },
            providesTags: (result) => result?.items
                ? [
                    ...result.items.map(s => ({type: ASSISTANT_SESSIONS_TYPE, id: s.id} as const)),
                    {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                ]
                : [{type: ASSISTANT_SESSIONS_TYPE, id: LIST}],
        }),
        getAssistantSession: build.query<IAssistantSession, string>({
            query: (id) => `ml/assistant/sessions/${id}`,
            providesTags: (_result, _error, id) => [{type: ASSISTANT_SESSIONS_TYPE, id}],
        }),
        /**
         * The raw transcript, for Adaptive Catalog staff debugging the assistant. The API refuses this
         * for everyone else, so the caller must not assume a 403 is an error worth surfacing loudly.
         *
         * Deliberately not tagged against the session: this is fetched on demand when the panel opens
         * and refetched by the panel itself, rather than being kept live by every session write.
         */
        getAssistantSessionTranscript: build.query<IAssistantTranscript, string>({
            query: (id) => `ml/assistant/sessions/${id}/transcript`,
        }),
        createAssistantSession: build.mutation<IAssistantSession, IAssistantSessionRequest>({
            query: (arg) => ({
                url: 'ml/assistant/sessions',
                method: 'POST',
                body: arg,
            }),
            invalidatesTags: () => [
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: ASSISTANT_USAGE_TYPE, id: LIST},
            ],
        }),
        /**
         * Queues one turn. The response is an ActionResponse, not the session: the work happens in
         * the LLM worker, so the dock learns the outcome by polling the session.
         */
        sendAssistantMessage: build.mutation<IActionResponse, {id: string, message: string}>({
            query: (arg) => ({
                url: `ml/assistant/sessions/${arg.id}/messages`,
                method: 'POST',
                body: {message: arg.message},
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: ASSISTANT_SESSIONS_TYPE, id: arg.id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: ASSISTANT_USAGE_TYPE, id: LIST},
            ],
        }),
        /**
         * Arms "notify me when it's done" for the CALLER: the API emails them once, if the build
         * reaches its outcome while they are not looking at it. A browser notification dies with the
         * tab, which is exactly the case this covers.
         *
         * The caller becomes the notify user, so a second person pressing the button takes the
         * notification over rather than joining it. That is the API's documented v1 rule.
         *
         * The session tag is invalidated so a dialog that stays open picks up notifyRequested and
         * renders the armed state without waiting for chance.
         */
        requestAssistantNotification: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/notify`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [{type: ASSISTANT_SESSIONS_TYPE, id}],
        }),
        /**
         * Records the human "yes" on a design proposal. The API re-queues the turn as part of this
         * call, so the session comes back as Pending and the dock must resume polling rather than
         * treating the returned session as final.
         */
        approveAssistantProposal: build.mutation<IAssistantSession, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/approve-proposal`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [
                {type: ASSISTANT_SESSIONS_TYPE, id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
            ],
        }),
        /** The only path that publishes on a session's behalf. The model has no publish tool. */
        approveAssistantPublish: build.mutation<IAssistantSession, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/approve-publish`,
                method: 'POST',
            }),
            invalidatesTags: (result, _error, id) => [
                {type: ASSISTANT_SESSIONS_TYPE, id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                ...(result?.targetId ? [{type: STOREFRONT_SETTINGS, id: result.targetId} as const] : []),
                {type: STOREFRONT_SETTINGS, id: LIST} as const,
                // Publishing closes the session's entry on the storefront's AI generation record, so
                // the storefronts table drops its chip and the settings sidebar lists the store again.
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION} as const,
            ],
        }),
        cancelAssistantSession: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/cancel`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [
                {type: ASSISTANT_SESSIONS_TYPE, id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION},
            ],
        }),
        /**
         * Puts a failed session back to work. The API flips it to Pending and re-queues the turn, so
         * the returned session is a starting point and not a result: the caller resumes polling.
         *
         * Nothing is torn down, so a retry picks up whatever the failed run had already built.
         */
        retryAssistantSession: build.mutation<IAssistantSession, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/retry`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [
                {type: ASSISTANT_SESSIONS_TYPE, id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION},
            ],
        }),
        /**
         * A testing affordance, not a user recovery path: cancels the session, deletes the storefront
         * it built, and starts a brand-new generation with the same settings, so a prompt or code
         * change can be exercised without retyping the form.
         *
         * The response is the NEW session, not the old one. The caller must switch to the returned
         * session id; polling the old id after this call only ever sees a cancelled session.
         *
         * Everything the cancel path tears down plus everything the create path builds is invalidated:
         * a storefront disappears and another one appears, and the new run draws on the AI quota.
         */
        tryAgainAssistantSession: build.mutation<IAssistantSession, string>({
            query: (id) => ({
                url: `ml/assistant/sessions/${id}/try-again`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, id) => [
                {type: ASSISTANT_SESSIONS_TYPE, id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: ASSISTANT_USAGE_TYPE, id: LIST},
                {type: STOREFRONT_SETTINGS, id: LIST},
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION},
            ],
        }),
        /**
         * Creates the storefront and starts a generation against it in one call, for the guided
         * create flow. The session comes back Pending, so the caller polls the session rather than
         * treating this response as the finished work.
         */
        createStorefrontGeneration: build.mutation<IAssistantSession, IStorefrontGenerationRequest>({
            query: (arg) => ({
                url: 'ml/assistant/storefront-generation',
                method: 'POST',
                body: arg,
            }),
            invalidatesTags: () => [
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: ASSISTANT_USAGE_TYPE, id: LIST},
                {type: STOREFRONT_SETTINGS, id: LIST},
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION},
            ],
        }),
        /**
         * Cancel carrying the "why are you starting over" survey.
         *
         * Kept alongside the bare cancelAssistantSession rather than replacing it: the chat dock has
         * no survey and its cancel must stay a one-argument call. The body is entirely optional, so
         * a skipped survey is a legitimate empty object.
         *
         * A cancelled storefront-generation session takes its created storefront with it, hence the
         * storefront list invalidation.
         */
        cancelAssistantSessionWithReason: build.mutation<IActionResponse, {id: string} & IAssistantCancelRequest>({
            query: (arg) => ({
                url: `ml/assistant/sessions/${arg.id}/cancel`,
                method: 'POST',
                body: {
                    ...(arg.reasonCode ? {reasonCode: arg.reasonCode} : {}),
                    ...(arg.comments ? {comments: arg.comments} : {}),
                },
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: ASSISTANT_SESSIONS_TYPE, id: arg.id},
                {type: ASSISTANT_SESSIONS_TYPE, id: LIST},
                {type: STOREFRONT_SETTINGS, id: LIST},
                {type: STOREFRONT_SETTINGS, id: AI_GENERATION},
            ],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetAssistantCapabilitiesQuery,
    useGetAssistantModelsQuery,
    useGetAssistantUsageQuery,
    useGetAssistantSessionsQuery,
    useGetAssistantSessionQuery,
    useLazyGetAssistantSessionTranscriptQuery,
    useCreateAssistantSessionMutation,
    useSendAssistantMessageMutation,
    useRequestAssistantNotificationMutation,
    useApproveAssistantProposalMutation,
    useApproveAssistantPublishMutation,
    useCancelAssistantSessionMutation,
    useRetryAssistantSessionMutation,
    useTryAgainAssistantSessionMutation,
    useCreateStorefrontGenerationMutation,
    useCancelAssistantSessionWithReasonMutation,
} = assistantEndpoints;

export default assistantEndpoints;

/**
 * Pull the operator-facing sentence out of a failed assistant call.
 *
 * The API answers refusals with an ActionResponse body: 429 for a rate limit (with the limit name
 * in `id`), 403 for a missing entitlement, 409 for a message sent mid-turn. Those messages are
 * written to be shown verbatim, so prefer them over anything invented here.
 */
export const assistantErrorMessage = (error: unknown, fallback: string): string => {
    const data = (error as {data?: {message?: string}} | undefined)?.data;
    if (data && typeof data.message === 'string' && data.message.length > 0) return data.message;
    return fallback;
};

/** The limit name from a 429 refusal (dailyRequests, generationWindow, ...), when there is one. */
export const assistantErrorLimit = (error: unknown): string | undefined => {
    const status = (error as {status?: number} | undefined)?.status;
    if (status !== 429) return undefined;
    const id = (error as {data?: {id?: string}} | undefined)?.data?.id;
    return typeof id === 'string' && id.length > 0 ? id : undefined;
};
